import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { useToast } from '../hooks/useToast';
import { getPricing, createPricing, updatePricing, deletePricing, recalculateCosts, getUnknownPricing } from '../services/costs';
import { setTitle } from '../utils/environment';

const PricingManagement = () => {
  const { user, logout } = useAuth();
  const { showError, showSuccess } = useToast();
  const [searchParams] = useSearchParams();
  const [pricing, setPricing] = useState([]);
  const [unknownPricing, setUnknownPricing] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [recalculating, setRecalculating] = useState(false);

  // Form state
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({
    provider: '',
    model: '',
    input_cost_per_1m: '',
    output_cost_per_1m: '',
    per_request_cost: '',
    effective_from: new Date().toISOString().split('T')[0]
  });

  // Recalculate form state
  const [recalcStartDate, setRecalcStartDate] = useState('');
  const [recalcEndDate, setRecalcEndDate] = useState('');
  const [recalcResult, setRecalcResult] = useState(null);
  const [forbidden, setForbidden] = useState(false);

  /**
   * Load pricing data from API
   */
  const loadData = async () => {
    try {
      setLoading(true);
      const [pricingData, unknownData] = await Promise.all([
        getPricing(),
        getUnknownPricing()
      ]);
      setPricing(pricingData);
      setUnknownPricing(unknownData);

      // Check URL params for pre-fill
      const provider = searchParams.get('provider');
      const model = searchParams.get('model');
      if (provider && model) {
        setFormData(prev => ({
          ...prev,
          provider,
          model
        }));
        setShowForm(true);
      }
    } catch (error) {
      console.error('Failed to load pricing:', error);
      if (error.response?.status === 403) {
        setForbidden(true);
      } else {
        showError(error.response?.data?.detail || 'Failed to load pricing data');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setTitle('Pricing Management');
    loadData();
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const resetForm = () => {
    setFormData({
      provider: '',
      model: '',
      input_cost_per_1m: '',
      output_cost_per_1m: '',
      per_request_cost: '',
      effective_from: new Date().toISOString().split('T')[0]
    });
    setEditingId(null);
    setShowForm(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setSaving(true);
      const data = {
        provider: formData.provider,
        model: formData.model,
        input_cost_per_1m: formData.input_cost_per_1m ? parseFloat(formData.input_cost_per_1m) : null,
        output_cost_per_1m: formData.output_cost_per_1m ? parseFloat(formData.output_cost_per_1m) : null,
        per_request_cost: formData.per_request_cost ? parseFloat(formData.per_request_cost) : null,
        effective_from: formData.effective_from
      };

      if (editingId) {
        await updatePricing(editingId, data);
        showSuccess('Pricing updated successfully');
      } else {
        await createPricing(data);
        showSuccess('Pricing created successfully');
      }
      resetForm();
      loadData();
    } catch (error) {
      console.error('Failed to save pricing:', error);
      showError(error.response?.data?.detail || 'Failed to save pricing');
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (item) => {
    setFormData({
      provider: item.provider,
      model: item.model,
      input_cost_per_1m: item.input_cost_per_1m || '',
      output_cost_per_1m: item.output_cost_per_1m || '',
      per_request_cost: item.per_request_cost || '',
      effective_from: item.effective_from
    });
    setEditingId(item.id);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this pricing entry?')) {
      return;
    }
    try {
      await deletePricing(id);
      showSuccess('Pricing deleted successfully');
      loadData();
    } catch (error) {
      console.error('Failed to delete pricing:', error);
      showError(error.response?.data?.detail || 'Failed to delete pricing');
    }
  };

  const handleRecalculate = async (e) => {
    e.preventDefault();
    if (!recalcStartDate || !recalcEndDate) {
      showError('Please select both start and end dates');
      return;
    }
    try {
      setRecalculating(true);
      const result = await recalculateCosts(recalcStartDate, recalcEndDate);
      setRecalcResult(result);
      showSuccess(`Recalculated ${result.updated_count} logs`);
    } catch (error) {
      console.error('Failed to recalculate costs:', error);
      showError(error.response?.data?.detail || 'Failed to recalculate costs');
    } finally {
      setRecalculating(false);
    }
  };

  const handleAddFromUnknown = (item) => {
    setFormData({
      provider: item.provider,
      model: item.model,
      input_cost_per_1m: '',
      output_cost_per_1m: '',
      per_request_cost: '',
      effective_from: new Date().toISOString().split('T')[0]
    });
    setEditingId(null);
    setShowForm(true);
  };

  if (forbidden) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="text-6xl mb-4">🔒</div>
          <h2 className="text-2xl font-semibold text-gray-900 mb-2">Admin Access Required</h2>
          <p className="text-gray-600 mb-4">You don&apos;t have permission to access pricing management.</p>
          <button
            onClick={() => window.history.back()}
            className="px-4 py-2 bg-blue-600 text-white rounded-md text-sm font-medium hover:bg-blue-700 transition-colors"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="spinner w-8 h-8 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading pricing data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div className="flex items-center">
              <button
                onClick={() => window.history.back()}
                className="mr-4 text-gray-600 hover:text-gray-900 transition-colors duration-200"
              >
                <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>
              <div className="h-10 w-10 mr-3">
                <img src={new URL('../assets/logo.png', import.meta.url).href} alt="Read It Never" className="h-10 w-10 rounded-xl" />
              </div>
              <div>
                <h1 className="text-xl font-semibold text-gray-900">Pricing Management</h1>
                <p className="text-sm text-gray-600">Configure LLM pricing for cost tracking</p>
              </div>
            </div>

            <div className="flex items-center space-x-4">
              <Link
                to="/admin/costs"
                className="flex items-center px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 transition-colors duration-200"
              >
                <svg className="w-4 h-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
                Cost Dashboard
              </Link>
              <span className="text-sm text-gray-600">{user?.email}</span>
              <button
                onClick={logout}
                className="text-sm text-gray-600 hover:text-gray-900 transition-colors duration-200"
              >
                Sign out
              </button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Unknown Pricing Alert */}
        {unknownPricing.length > 0 && (
          <section className="mb-8">
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-4">
              <div className="flex items-start">
                <svg className="w-5 h-5 text-amber-600 mt-0.5 mr-3 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
                <div>
                  <h3 className="text-sm font-medium text-amber-800">Models Missing Pricing</h3>
                  <p className="text-sm text-amber-700 mt-1">
                    Add pricing for these models to enable cost tracking.
                  </p>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Provider</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Model</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Requests</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Action</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {unknownPricing.map((item, idx) => (
                    <tr key={idx} className="bg-amber-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 capitalize">{item.provider}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 font-mono">{item.model}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">{item.count.toLocaleString()}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-right">
                        <button
                          onClick={() => handleAddFromUnknown(item)}
                          className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                        >
                          Add Pricing
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        )}

        {/* Add/Edit Form */}
        <section className="mb-8">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-2xl font-semibold text-gray-900">
              {showForm ? (editingId ? 'Edit Pricing' : 'Add Pricing') : 'Pricing Configuration'}
            </h2>
            {!showForm && (
              <button
                onClick={() => setShowForm(true)}
                className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-md text-sm font-medium hover:bg-blue-700 transition-colors"
              >
                <svg className="w-4 h-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                Add Pricing
              </button>
            )}
          </div>

          {showForm && (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
              <form onSubmit={handleSubmit}>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Provider</label>
                    <select
                      name="provider"
                      value={formData.provider}
                      onChange={handleInputChange}
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="">Select provider</option>
                      <option value="openai">OpenAI</option>
                      <option value="gemini">Gemini</option>
                      <option value="anthropic">Anthropic</option>
                      <option value="vertex_ai">Vertex AI</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Model</label>
                    <input
                      type="text"
                      name="model"
                      value={formData.model}
                      onChange={handleInputChange}
                      required
                      placeholder="e.g., gpt-4o-mini"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Effective From</label>
                    <input
                      type="date"
                      name="effective_from"
                      value={formData.effective_from}
                      onChange={handleInputChange}
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Input Cost per 1M Tokens ($)</label>
                    <input
                      type="number"
                      name="input_cost_per_1m"
                      value={formData.input_cost_per_1m}
                      onChange={handleInputChange}
                      step="0.000001"
                      min="0"
                      placeholder="e.g., 0.15"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Output Cost per 1M Tokens ($)</label>
                    <input
                      type="number"
                      name="output_cost_per_1m"
                      value={formData.output_cost_per_1m}
                      onChange={handleInputChange}
                      step="0.000001"
                      min="0"
                      placeholder="e.g., 0.60"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Per Request Cost ($)</label>
                    <input
                      type="number"
                      name="per_request_cost"
                      value={formData.per_request_cost}
                      onChange={handleInputChange}
                      step="0.000001"
                      min="0"
                      placeholder="For non-token pricing"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>
                <div className="mt-4 flex space-x-3">
                  <button
                    type="submit"
                    disabled={saving}
                    className="px-4 py-2 bg-blue-600 text-white rounded-md text-sm font-medium hover:bg-blue-700 transition-colors disabled:opacity-50"
                  >
                    {saving ? 'Saving...' : (editingId ? 'Update' : 'Create')}
                  </button>
                  <button
                    type="button"
                    onClick={resetForm}
                    className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md text-sm font-medium hover:bg-gray-50 transition-colors"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          )}

          {/* Pricing Table */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
            {pricing.length > 0 ? (
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Provider</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Model</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Input/1M</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Output/1M</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Per Request</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Effective From</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {pricing.map((item) => (
                    <tr key={item.id}>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 capitalize">{item.provider}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 font-mono">{item.model}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">
                        {item.input_cost_per_1m ? `$${item.input_cost_per_1m}` : '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">
                        {item.output_cost_per_1m ? `$${item.output_cost_per_1m}` : '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">
                        {item.per_request_cost ? `$${item.per_request_cost}` : '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 text-right">{item.effective_from}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm space-x-3">
                        <button
                          onClick={() => handleEdit(item)}
                          className="text-blue-600 hover:text-blue-800 font-medium"
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => handleDelete(item.id)}
                          className="text-red-600 hover:text-red-800 font-medium"
                        >
                          Delete
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <p className="text-gray-600 text-center italic py-8">No pricing configured. Add pricing to start tracking costs.</p>
            )}
          </div>
        </section>

        {/* Recalculate Section */}
        <section className="mb-8">
          <h2 className="text-2xl font-semibold text-gray-900 mb-4">Recalculate Costs</h2>
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <p className="text-sm text-gray-600 mb-4">
              Recalculate costs for existing usage logs using current pricing. Useful after adding or updating pricing for a model.
            </p>
            <form onSubmit={handleRecalculate} className="flex items-end space-x-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Start Date</label>
                <input
                  type="date"
                  value={recalcStartDate}
                  onChange={(e) => setRecalcStartDate(e.target.value)}
                  required
                  className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">End Date</label>
                <input
                  type="date"
                  value={recalcEndDate}
                  onChange={(e) => setRecalcEndDate(e.target.value)}
                  required
                  className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <button
                type="submit"
                disabled={recalculating}
                className="px-4 py-2 bg-blue-600 text-white rounded-md text-sm font-medium hover:bg-blue-700 transition-colors disabled:opacity-50"
              >
                {recalculating ? 'Recalculating...' : 'Recalculate'}
              </button>
            </form>
            {recalcResult && (
              <div className="mt-4 p-4 bg-green-50 border border-green-200 rounded-md">
                <p className="text-sm text-green-800">
                  Processed {recalcResult.total_processed.toLocaleString()} logs.
                  Updated {recalcResult.updated_count.toLocaleString()} with new costs.
                  {recalcResult.still_unknown > 0 && (
                    <span className="text-amber-700"> {recalcResult.still_unknown.toLocaleString()} still missing pricing.</span>
                  )}
                  {recalcResult.total_cost_change !== 0 && (
                    <span> Total cost change: ${recalcResult.total_cost_change.toFixed(2)}</span>
                  )}
                </p>
              </div>
            )}
          </div>
        </section>

        {/* Pricing Reference */}
        <section className="mb-8">
          <h2 className="text-2xl font-semibold text-gray-900 mb-4">Pricing Reference</h2>
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <p className="text-sm text-gray-600 mb-4">Common model pricing (per 1M tokens, as of 2024):</p>
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className="py-2 text-left font-medium text-gray-700">Provider</th>
                    <th className="py-2 text-left font-medium text-gray-700">Model</th>
                    <th className="py-2 text-right font-medium text-gray-700">Input</th>
                    <th className="py-2 text-right font-medium text-gray-700">Output</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  <tr><td className="py-2">OpenAI</td><td className="font-mono">gpt-4o-mini</td><td className="text-right">$0.15</td><td className="text-right">$0.60</td></tr>
                  <tr><td className="py-2">OpenAI</td><td className="font-mono">text-embedding-3-small</td><td className="text-right">$0.02</td><td className="text-right">-</td></tr>
                  <tr><td className="py-2">Gemini</td><td className="font-mono">gemini-3-flash-preview</td><td className="text-right">$0.10</td><td className="text-right">$0.40</td></tr>
                  <tr><td className="py-2">Gemini</td><td className="font-mono">gemini-2.0-flash</td><td className="text-right">$0.075</td><td className="text-right">$0.30</td></tr>
                  <tr><td className="py-2">Anthropic</td><td className="font-mono">claude-3-haiku-20240307</td><td className="text-right">$0.25</td><td className="text-right">$1.25</td></tr>
                  <tr><td className="py-2">Vertex AI</td><td className="font-mono">multimodalembedding@001</td><td className="text-right" colSpan="2">~$0.0001/image</td></tr>
                </tbody>
              </table>
            </div>
          </div>
        </section>
      </main>
    </div>
  );
};

export default PricingManagement;
